import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link as ScrollLink } from "react-scroll";
import { useContent } from "../context/ContentContext";

const navLinks = [
  { label: "About", to: "about" },
  { label: "Products", to: "services" },
  { label: "Contact", to: "contact" },
];

export default function Topbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleDrawerToggle = () => setDrawerOpen((prev) => !prev);

  const logo = useContent().getCompany()?.logo;

  // 👇 Scroll effect for shrinking Topbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 30) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box sx={{ position: "relative" }}>
      <AppBar 
        position="fixed"
        elevation={scrolled ? 2 : 0}
        sx={{
          background: "#fff",
          transition: "all 0.3s ease",
        }}
      >
        <Toolbar
          sx={{
            gap: 2,
            justifyContent: "space-between",
            minHeight: scrolled ? 56 : { xs: 66, sm: 74 }, // ✅ shrink height on scroll
            transition: "all 0.3s ease",
          }}
        >
          {/* Logo and Title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              flexGrow: 1,
               overflow: "hidden", // ✅ Prevents content overflow on scroll
            }}
          >
            <img
              src={logo}
              alt="Logo"
              style={{
                height: scrolled ? "36px" : "48px", // ✅ consistent and smaller size on scroll
                width: "auto",
                maxWidth: 500,
                objectFit: "contain",
                transition: "height 0.3s ease",
              }}
            />
            <Box
              sx={{
                ml: 2,
                fontWeight: 700,
                fontSize: scrolled ? 14 : { xs: 14, sm: 18 },
                color: "#000000",
                letterSpacing: 1,
                whiteSpace: "nowrap",
                fontFamily: "'Montserrat', 'Arial', sans-serif",
                transition: "all 0.3s ease",
              }}
            >
              ADVERTISING & PRINTING
            </Box>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2 }}>
            {navLinks.map((item) => (
              <ScrollLink
                key={item.to}
                to={item.to}
                smooth
                duration={500}
                offset={-70}
                style={{ textDecoration: "none" }}
              >
                <Button
                  color="inherit"
                  sx={{
                    color: "#000",
                    fontWeight: 600,
                    backgroundColor: "transparent",
                    borderBottom: "4px solid transparent",
                    borderRadius: 0,
                    transition: "border-color 0.2s",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: "#ff6f00",
                      borderBottom: "4px solid #ff6f00",
                    },
                  }}
                >
                  {item.label}
                </Button>
              </ScrollLink>
            ))}
          </Box>

          {/* Mobile Menu Icon */}
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={handleDrawerToggle}
            sx={{
              display: { xs: "inline-flex", md: "none" },
              color: "#000",
              ml: 1,
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          PaperProps={{
            sx: {
              width: 200,
              background: "#ff6f00",
              color: "#fff",
            },
          }}
        >
          <List>
            {navLinks.map((item) => (
              <ScrollLink
                key={item.to}
                to={item.to}
                smooth
                duration={500}
                offset={-70}
                onClick={handleDrawerToggle}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <ListItem
                  button
                  sx={{
                    backgroundColor: "transparent",
                    transition: "border-color 0.2s",
                    borderBottom: "4px solid transparent",
                    borderRadius: 0,
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: "#fff",
                      borderBottom: "4px solid #fff",
                    },
                  }}
                >
                  <ListItemText primary={item.label} />
                </ListItem>
              </ScrollLink>
            ))}
          </List>
        </Drawer>
      </AppBar>

      {/* Sticky bottom black border */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: "4px",
          bgcolor: "#000",
          zIndex: 1,
        }}
      />
    </Box>
  );
}
